/**
 * Comensales simulados — no representan ventas ni clientes reales.
 */
export type GuestPhase = 'entering' | 'waiting' | 'seated' | 'dining' | 'leaving';

export interface GuestMarker {
  id: string;
  x: number;
  z: number;
  phase: GuestPhase;
  partyId: string;
}

export interface GuestParty {
  id: string;
  size: number;
  phase: GuestPhase;
  tableId: number | null;
  waitSec: number;
  dineSec: number;
}

const ENTRANCE = { x: 0, z: 12 };
const WAIT = { x: 2, z: 9 };

export class GuestSimulation {
  private parties: GuestParty[] = [];
  private seq = 0;
  private markers: GuestMarker[] = [];

  getMarkers(): GuestMarker[] {
    return this.markers.map((m) => ({ ...m }));
  }

  getParties(): GuestParty[] {
    return this.parties.map((p) => ({ ...p }));
  }

  reset(): void {
    this.parties = [];
    this.markers = [];
    this.seq = 0;
  }

  spawnParty(size: number): string {
    this.seq += 1;
    const id = `party-${this.seq}`;
    this.parties.push({
      id,
      size: Math.max(1, Math.min(8, size)),
      phase: 'entering',
      tableId: null,
      waitSec: 0,
      dineSec: 0,
    });
    this.rebuildMarkersFromTables(new Map());
    return id;
  }

  /** Asigna mesa cuando el motor de piso confirma asiento. */
  assignTable(partyId: string, tableId: number, seat: { x: number; z: number }): void {
    const p = this.parties.find((x) => x.id === partyId);
    if (!p) return;
    p.tableId = tableId;
    p.phase = 'seated';
    this.rebuildMarkers(seat);
  }

  tick(dt: number, tablePositions: Map<number, { x: number; z: number }>): void {
    for (const p of this.parties) {
      if (p.phase === 'entering') {
        p.waitSec += dt;
        if (p.waitSec > 2.5) p.phase = 'waiting';
      } else if (p.phase === 'waiting') {
        p.waitSec += dt;
      } else if (p.phase === 'seated') {
        p.dineSec += dt;
        if (p.dineSec > 4) p.phase = 'dining';
      } else if (p.phase === 'dining') {
        p.dineSec += dt;
        if (p.dineSec > 14) p.phase = 'leaving';
      } else if (p.phase === 'leaving') {
        p.waitSec += dt;
        if (p.waitSec > 18) {
          this.parties = this.parties.filter((x) => x.id !== p.id);
        }
      }
    }
    this.rebuildMarkersFromTables(tablePositions);
  }

  private rebuildMarkers(anchor: { x: number; z: number }): void {
    this.markers = [];
    for (const p of this.parties) {
      for (let i = 0; i < p.size; i += 1) {
        const offset = (i - (p.size - 1) / 2) * 0.55;
        this.markers.push({
          id: `${p.id}-g${i}`,
          partyId: p.id,
          phase: p.phase,
          x: anchor.x + offset,
          z: anchor.z + (i % 2) * 0.35,
        });
      }
    }
  }

  private rebuildMarkersFromTables(tablePositions: Map<number, { x: number; z: number }>): void {
    this.markers = [];
    for (const p of this.parties) {
      let base = ENTRANCE;
      if (p.phase === 'waiting') base = WAIT;
      if (p.tableId != null && tablePositions.has(p.tableId)) {
        base = tablePositions.get(p.tableId)!;
      }
      if (p.phase === 'leaving') {
        base = { x: ENTRANCE.x + (p.waitSec % 3), z: ENTRANCE.z + 2 };
      }
      for (let i = 0; i < p.size; i += 1) {
        const offset = (i - (p.size - 1) / 2) * 0.55;
        this.markers.push({
          id: `${p.id}-g${i}`,
          partyId: p.id,
          phase: p.phase,
          x: base.x + offset,
          z: base.z + (i % 2) * 0.35,
        });
      }
    }
  }
}
